import React, { useState, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    IconButton,
    Tooltip,
} from '@mui/material';
import {
    UnfoldMore as UnfoldMoreIcon,
    UnfoldLess as UnfoldLessIcon,
} from '@mui/icons-material';
import {
    updateAssetSlider,
    toggleAssetSelection,
    clearSubmitError,
    partialSuccessCleanup,
    updateAssetByAmount
} from '../../../../Redux/cashout/cashoutSlice';
import { SelectedAssetsProps, SubmitCashoutResult } from '../../../../types/cashout.types';
import { submitCashoutRequest } from '../../../../Redux/cashout/cashoutThunks';
import SelectedAssetItem from './SelectedAssetItem';
import SubmitErrorPanel from './SubmitErrorPanel';
import { usePriceFormatter } from '../../../../hooks';
import { useAppDispatch, useAppSelector } from '../../../../Redux/hooks';

export default function SelectedAssets({
    onCashoutSuccess,
    onCashoutFailed,
    onRefreshAssets,
    refreshing = false,
}: SelectedAssetsProps) {
    const dispatch = useAppDispatch();
    const {
        selectedAssets,
        availableAssets,
        totalEstimatedCashOut,
        loading,
        submitError,
        submitDisabled,
    } = useAppSelector((state) => state.cashout);
    const formatter = usePriceFormatter();

    const [expandedAssets, setExpandedAssets] = useState<Set<string>>(new Set());

    const handleSliderChange = (assetId: string, value: number | number[]) => {
        const sliderValue = typeof value === 'number' ? value : value[0];
        dispatch(updateAssetSlider({ assetId, sliderValue }));
    };

const handleAmountChange = (assetId: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const amountUSD = parseFloat(event.target.value) || 0;
    dispatch(updateAssetByAmount({ assetId, amountUSD }));
};

    const handleRemoveAsset = (assetId: string) => {
        const asset = availableAssets.find(a => a.id === assetId);
        if (asset) {
            dispatch(toggleAssetSelection(asset));
            setExpandedAssets(prev => {
                const next = new Set(prev);
                next.delete(assetId);
                return next;
            });
        }
    };

    const toggleExpand = (assetId: string) => {
        setExpandedAssets(prev => {
            const next = new Set(prev);
            next.has(assetId) ? next.delete(assetId) : next.add(assetId);
            return next;
        });
    };

    const handleExpandCollapseAll = () => {
        if (expandedAssets.size === selectedAssets.length) {
            setExpandedAssets(new Set());
        } else {
            setExpandedAssets(new Set(selectedAssets.map(a => a.id)));
        }
    };

    const allExpanded = selectedAssets.length > 0 && expandedAssets.size === selectedAssets.length;

    const handlePartialSuccessCleanup = useCallback(
        (succeededIds: string[], _failedIds: string[]) => {
            // Remove succeeded assets from Redux selection by their unique id.
            // Failed assets stay in selectedAssets so their red highlight persists.
            dispatch(partialSuccessCleanup(succeededIds));
        },
        [dispatch]
    );

    const handleCashOut = async () => {
        if (selectedAssets.length === 0) return;

        const result = await dispatch(
            submitCashoutRequest({
                selectedAssets,
                totalAmount: totalEstimatedCashOut,
            })
        );

        if (submitCashoutRequest.fulfilled.match(result)) {
            // 200 full success — slice already cleared selectedAssets
            onCashoutSuccess?.();
            onRefreshAssets?.();
        } else if (submitCashoutRequest.rejected.match(result)) {
            const structured = result.payload as SubmitCashoutResult;

            // Match by assetId (unique) — handles LOAN + USER_OWNED same symbol correctly
            const succeededIds = (structured?.success ?? [])
                .map((s: any) => s.assetId)
                .filter(Boolean) as string[];

            const failedAssets = structured?.failed ?? [];

            if (succeededIds.length > 0) {
                // Partial success: remove succeeded from selection, keep failed ones
                handlePartialSuccessCleanup(succeededIds, []);
                onCashoutSuccess?.(failedAssets);
                onRefreshAssets?.();
            } else {
                // Full failure: keep entire selection intact
                onCashoutFailed?.(failedAssets);
            }
        }
    };

    const isBusy = loading || refreshing;
    const isButtonDisabled = isBusy || submitDisabled || selectedAssets.length === 0;

    const hasPartialSuccess =
        submitError && submitError.success.length > 0 && submitError.failed.length > 0;

    return (
        <Box
            sx={{
                position: { xs: 'relative', lg: 'absolute' },
                left: { xs: '0', lg: '67.36%' },
                top: { xs: 'auto', lg: '0' },
                width: { xs: '100%', lg: 'auto' },
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Box
                sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: { xs: '20px', sm: '30px' },
                    padding: { xs: '20px', sm: '30px' },
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: { xs: '70vh', sm: '600px', lg: 'calc(100vh - 150px)' },
                    minWidth: { xs: '100%', sm: '400px', md: '450px' },
                    minHeight: { xs: '350px', sm: '400px', md: '450px' },
                    overflow: 'hidden',
                }}
            >
                {/* ── Fixed Header ─────────────────────────────────────────── */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '12px', sm: '16px' }, flexShrink: 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography
                            sx={{
                                fontSize: { xs: '20px', sm: '24px' },
                                fontWeight: '400',
                                lineHeight: { xs: '28px', sm: '33px' },
                                color: '#FFFFFF',
                                fontFamily: 'Avenir',
                            }}
                        >
                            Selected Assets
                        </Typography>

                        {selectedAssets.length > 0 && (
                            <Tooltip title={allExpanded ? 'Collapse All' : 'Expand All'} arrow>
                                <IconButton
                                    onClick={handleExpandCollapseAll}
                                    size="small"
                                    sx={{
                                        width: { xs: '32px', sm: '36px' },
                                        height: { xs: '32px', sm: '36px' },
                                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                        border: '1px solid rgba(255, 255, 255, 0.1)',
                                        borderRadius: '8px',
                                        color: 'rgba(255, 255, 255, 0.7)',
                                    }}
                                >
                                    {allExpanded
                                        ? <UnfoldLessIcon sx={{ fontSize: { xs: '18px', sm: '20px' } }} />
                                        : <UnfoldMoreIcon sx={{ fontSize: { xs: '18px', sm: '20px' } }} />
                                    }
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>

                    {/* ── Structured error panel ──────────────────────────── */}
                    {submitError && submitError.failed.length > 0 && (
                        <SubmitErrorPanel
                            submitError={submitError}
                            onDismiss={() => dispatch(clearSubmitError())}
                        />
                    )}

                    {/* ── Refreshing banner ────────────────────────────────── */}
                    {refreshing && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                padding: '10px 14px',
                                backgroundColor: 'rgba(74, 122, 242, 0.12)',
                                border: '1px solid rgba(74, 122, 242, 0.3)',
                                borderRadius: '10px',
                            }}
                        >
                            <CircularProgress size={14} sx={{ color: '#4A7AF2' }} />
                            <Typography sx={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', fontFamily: 'Avenir' }}>
                                Refreshing available assets…
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* ── Scrollable Assets List ───────────────────────────────── */}
                <Box
                    sx={{
                        flex: 1,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        marginTop: { xs: '15px', sm: '20px' },
                        marginBottom: selectedAssets.length > 0 ? { xs: '15px', sm: '20px' } : 0,
                        scrollbarWidth: 'thin',
                        scrollbarColor: 'rgba(255, 255, 255, 0.3) transparent',
                        '&::-webkit-scrollbar': { width: '8px' },
                        '&::-webkit-scrollbar-track': { background: 'transparent', borderRadius: '10px' },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            borderRadius: '10px',
                            border: '2px solid transparent',
                            backgroundClip: 'padding-box',
                        },
                        '&::-webkit-scrollbar-thumb:hover': { backgroundColor: 'rgba(255, 255, 255, 0.35)' },
                        pr: { xs: '8px', sm: '12px' },
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '12px', sm: '15px' } }}>
                        {selectedAssets.length === 0 ? (
                            <Typography
                                sx={{
                                    color: 'rgba(255, 255, 255, 0.5)',
                                    fontSize: { xs: '13px', sm: '14px' },
                                    textAlign: 'center',
                                    padding: { xs: '40px 15px', sm: '60px 20px' },
                                }}
                            >
                                No assets selected. Select assets from the table to get started.
                            </Typography>
                        ) : (
                            selectedAssets.map((asset) => {
                                // Highlight assets that failed in the last submit
                                const assetError = submitError?.failed.find(f => f.asset === asset.symbol);
                                return (
                                    <SelectedAssetItem
                                        key={asset.id}
                                        asset={asset}
                                        isExpanded={expandedAssets.has(asset.id)}
                                        hasError={!!assetError}
                                        errorMessage={assetError?.error}
                                        onToggleExpand={() => toggleExpand(asset.id)}
                                        onSliderChange={(v) => handleSliderChange(asset.id, v)}
                                        onAmountChange={(e) => handleAmountChange(asset.id, e)}
                                        onRemove={() => handleRemoveAsset(asset.id)}
                                    />
                                );
                            })
                        )}
                    </Box>
                </Box>

                {/* ── Fixed Footer ─────────────────────────────────────────── */}
                {selectedAssets.length > 0 && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: { xs: '12px', sm: '15px' },
                            flexShrink: 0,
                            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                            paddingTop: { xs: '15px', sm: '20px' },
                        }}
                    >
                        {/* Re-submit hint when button is locked */}
                        {/* {submitDisabled && !refreshing && (
                            <Typography
                                sx={{
                                    fontSize: '12px',
                                    color: 'rgba(255,193,7,0.85)',
                                    fontFamily: 'Avenir',
                                    textAlign: 'center',
                                }}
                            >
                                Adjust amounts or remove failed assets to re-enable submission.
                            </Typography>
                        )} */}

                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                                gap: { xs: 1, sm: 0 },
                            }}
                        >
                            <Typography sx={{ fontSize: { xs: '13px', sm: '14px' }, color: 'rgba(255, 255, 255, 0.5)', fontFamily: 'Avenir' }}>
                                Total Estimated Cash Out
                            </Typography>
                            <Typography sx={{ fontSize: { xs: '20px', sm: '24px' }, fontWeight: '800', color: '#4EDF6D', fontFamily: 'Avenir' }}>
                                {formatter.amount(totalEstimatedCashOut).display}
                            </Typography>
                        </Box>

                        <Tooltip
                            title={
                                submitDisabled && !isBusy
                                    ? 'Adjust your selection or amounts to re-enable'
                                    : ''
                            }
                            arrow
                        >
                            {/* Wrap in span so Tooltip works even when button is disabled */}
                            <span style={{ width: '100%' }}>
                                <Button
                                    variant="contained"
                                    onClick={handleCashOut}
                                    disabled={isButtonDisabled}
                                    fullWidth
                                    sx={{
                                        backgroundColor: isButtonDisabled ? undefined : '#4EDF6D',
                                        color: '#141414',
                                        fontWeight: '800',
                                        fontSize: { xs: '14px', sm: '16px' },
                                        padding: { xs: '10px 20px', sm: '12px 24px' },
                                        borderRadius: '1234px',
                                        textTransform: 'none',
                                        fontFamily: 'Avenir',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            backgroundColor: '#45CC61',
                                            transform: 'translateY(-1px)',
                                            boxShadow: '0 4px 12px rgba(78, 223, 109, 0.3)',
                                        },
                                        '&:active': { transform: 'translateY(0)' },
                                        '&:disabled': {
                                            backgroundColor: submitDisabled
                                                ? 'rgba(255, 107, 107, 0.25)'   // red tint when locked by error
                                                : 'rgba(78, 223, 109, 0.3)',    // dim green when just loading
                                            color: submitDisabled
                                                ? 'rgba(255,107,107,0.6)'
                                                : '#141414',
                                        },
                                    }}
                                >
                                    {isBusy ? (
                                        <CircularProgress size={20} sx={{ color: submitDisabled ? '#FF6B6B' : '#141414' }} />
                                    ) : submitDisabled ? (
                                        'Submission Locked'
                                    ) : (
                                        'Cash Out'
                                    )}
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>
                )}
            </Box>
        </Box>
    );
}
